(function () {
  const STORAGE_KEY = 'craft_cellars_selected_location';

  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Ignore storage access errors (private mode, blocked storage, etc.)
  }

  function getStoredSelection() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function setStoredSelection(selection) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
    document.documentElement.dataset.craftCellarsLocation = selection.key;
    window.CraftCellarsStorefrontApi?.clearCache?.();
    document.dispatchEvent(
      new CustomEvent('craft-cellars:location-selected', { detail: selection, bubbles: true })
    );
  }

  window.CraftCellarsLocation = {
    storageKey: STORAGE_KEY,
    getSelection: getStoredSelection,
    setSelection: setStoredSelection,

    syncHeader() {
      const selection = getStoredSelection();
      if (!selection) return;
      // console.log('Syncing header with selection:', selection);
      document.querySelectorAll('[data-location-picker-trigger]').forEach(button => {
        const isSelected = button.dataset.locationKey === selection.key;

        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', isSelected);

        const address = button.querySelector('[data-location-address]');
        // console.log(address, selection.name, isSelected);
        if (address && isSelected) {
          address.textContent = address.textContent;
        }
      });
    },

    clearSelection() {
      localStorage.removeItem(STORAGE_KEY);
      delete document.documentElement.dataset.craftCellarsLocation;
    },
  };

  if (!customElements.get('location-selector-modal')) {
    customElements.define(
      'location-selector-modal',
      class LocationSelectorModal extends HTMLElement {
        constructor() {
          super();
          this.onShopClick = this.onShopClick.bind(this);
          this.onKeyUp = this.onKeyUp.bind(this);
          this.onSliderPrev = this.onSliderPrev.bind(this);
          this.onSliderNext = this.onSliderNext.bind(this);
          this.updateSliderState = this.updateSliderState.bind(this);
          this.onLocationSelected = this.onLocationSelected.bind(this);
          this.onPageShow = this.onPageShow.bind(this);
        }

        connectedCallback() {
          this.config = window.craftCellarsLocationConfig;
          if (!this.config?.enabled) return;

          this.overlay = this;
          this.dialog = this.querySelector('.location-selector-modal__dialog');
          this.closeButton = this.querySelector('.location-selector-modal__close');
          this.grid = this.querySelector('.location-selector-modal__grid');
          this.prevButton = this.querySelector('[data-location-slider-prev]');
          this.nextButton = this.querySelector('[data-location-slider-next]');
          this.pagination = this.querySelector('.location-selector-modal__pagination');
          this.cards = Array.from(this.querySelectorAll('[data-location-key]'));

          this.cards.forEach(card => {
            card.querySelector('[data-shop-now]')?.addEventListener('click', this.onShopClick);
          });

          if (this.closeButton) {
            this.closeButton.hidden = !(this.config.allowDismiss || getStoredSelection());
            this.closeButton.addEventListener('click', () => this.hide());
          }

          this.prevButton?.addEventListener('click', this.onSliderPrev);
          this.nextButton?.addEventListener('click', this.onSliderNext);
          this.grid?.addEventListener('scroll', this.updateSliderState, { passive: true });
          window.addEventListener('resize', this.updateSliderState);
          window.addEventListener('pageshow', this.onPageShow);
          document.addEventListener('craft-cellars:location-selected', this.onLocationSelected);

          this.syncSelectedCards();
          this.createPagination();
          this.updateSliderState();
          this.maybeShow();
        }

        disconnectedCallback() {
          this.prevButton?.removeEventListener('click', this.onSliderPrev);
          this.nextButton?.removeEventListener('click', this.onSliderNext);
          this.grid?.removeEventListener('scroll', this.updateSliderState);
          window.removeEventListener('resize', this.updateSliderState);
          window.removeEventListener('pageshow', this.onPageShow);
          document.removeEventListener('craft-cellars:location-selected', this.onLocationSelected);
        }

        onPageShow(event) {
          if (!event.persisted) return;
          this.maybeShow();
        }

        syncSelectedCards(selection = getStoredSelection()) {
          const activeKey = selection?.key || this.config.defaultLocationKey;
          this.cards.forEach(card => {
            const isSelected = card.dataset.locationKey === activeKey;
            card.classList.toggle('is-selected', isSelected);
            card.classList.toggle(
              'is-default',
              !selection && card.dataset.locationKey === this.config.defaultLocationKey
            );
          });
        }

        onLocationSelected(event) {
          this.syncSelectedCards(event.detail || getStoredSelection());
        }

        maybeShow() {
          const stored = getStoredSelection();

          if (stored) {
            document.documentElement.dataset.craftCellarsLocation = stored.key;
            window.CraftCellarsLocation?.syncHeader?.();
          }

          if (this.config.forceShowInEditor || this.config.isHomepage || !stored) {
            this.show();
          }
        }

        show() {
          const selection = getStoredSelection();
          const focusKey = selection?.key || this.config.defaultLocationKey;
          this.syncSelectedCards(selection);
          if (this.closeButton) {
            this.closeButton.hidden = !(this.config.allowDismiss || selection);
          }
          document.body.classList.add('overflow-hidden');
          this.setAttribute('open', '');
          this.addEventListener('keyup', this.onKeyUp);
          const focusTarget =
            this.querySelector(`[data-location-key="${focusKey}"] [data-shop-now]`) ||
            this.querySelector('[data-shop-now]');
          if (typeof trapFocus === 'function') trapFocus(this.dialog, focusTarget);
          this.updateSliderState();
        }

        hide() {
          if (!this.config.allowDismiss && !getStoredSelection()) return;
          document.body.classList.remove('overflow-hidden');
          document.body.classList.remove('overflow-hidden-tablet');
          this.removeAttribute('open');
          this.removeEventListener('keyup', this.onKeyUp);
          if (typeof removeTrapFocus === 'function') removeTrapFocus();
        }

        onKeyUp(event) {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        }

        isMobileSlider() {
          return window.matchMedia('(max-width: 989px)').matches;
        }

        getSlideOffset() {
          if (!this.cards.length) return 0;
          const cardWidth = this.cards[0].getBoundingClientRect().width;
          const styles = window.getComputedStyle(this.grid);
          const gap = parseFloat(styles.columnGap || styles.gap || '0');
          return cardWidth + gap;
        }

        createPagination() {
          if (!this.pagination || !this.cards.length) return;
          this.pagination.innerHTML = '';

          this.cards.forEach((_, index) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'location-selector-modal__pagination-dot';
            dot.dataset.index = String(index);
            dot.setAttribute('aria-label', `Go to location ${index + 1}`);
            dot.addEventListener('click', () => {
              if (!this.isMobileSlider()) return;
              this.grid.scrollTo({ left: index * this.getSlideOffset(), behavior: 'smooth' });
            });
            this.pagination.appendChild(dot);
          });
          this.updatePagination();
        }

        updatePagination() {
          if (!this.pagination || !this.cards.length) return;

          const dots = Array.from(this.pagination.children);
          if (!this.isMobileSlider()) {
            dots.forEach(dot => dot.classList.remove('is-active'));
            return;
          }

          const current = this.grid.scrollLeft;
          const offset = this.getSlideOffset() || 1;
          const rawIndex = Math.round(current / offset);
          const maxIndex = this.cards.length - 1;
          const activeIndex = Math.min(maxIndex, Math.max(0, rawIndex));

          dots.forEach((dot, index) => {
            dot.classList.toggle('is-active', index === activeIndex);
          });
        }

        onSliderPrev() {
          if (!this.grid || !this.isMobileSlider()) return;
          this.grid.scrollBy({ left: -this.getSlideOffset(), behavior: 'smooth' });
        }

        onSliderNext() {
          if (!this.grid || !this.isMobileSlider()) return;
          this.grid.scrollBy({ left: this.getSlideOffset(), behavior: 'smooth' });
        }

        updateSliderState() {
          if (!this.grid || !this.prevButton || !this.nextButton) return;

          if (!this.isMobileSlider()) {
            this.prevButton.disabled = true;
            this.nextButton.disabled = true;
            this.updatePagination();
            return;
          }

          const maxScroll = this.grid.scrollWidth - this.grid.clientWidth;
          const current = this.grid.scrollLeft;
          const threshold = 6;
          this.prevButton.disabled = current <= threshold;
          this.nextButton.disabled = current >= maxScroll - threshold;
          this.updatePagination();
        }

        onShopClick(event) {
          const card = event.currentTarget.closest('[data-location-key]');
          const cardUrl = card ? card.querySelector('[data-shop-now]')?.href : null;

          if (!card) return;
          if (card.dataset.locationKey === 'location_3') {
            this.hide();
            return;
          }

          const selection = {
            key: card.dataset.locationKey,
            type: card.dataset.locationType,
            id: card.dataset.locationId,
            name: card.dataset.locationName,
            address: card.dataset.locationAddress,
          };

          setStoredSelection(selection);

          document.querySelectorAll('[data-location-picker-trigger]').forEach(button => {
            const isSelected = button.dataset.locationKey === selection.key;

            button.classList.toggle('is-selected', isSelected);
            button.setAttribute('aria-pressed', isSelected);

            const address = button.querySelector('[data-location-address]');
            if (address && isSelected) {
              address.textContent = address.textContent;
            }
          });

          this.cards.forEach(c => c.classList.toggle('is-selected', c === card));
          window.CraftCellarsLocation?.syncHeader();
          this.hide();

          if (this.config.isHomepage && cardUrl === window.location.href) {
            event.preventDefault();
          }
        }
      }
    );
  }
})();
